import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

let ReactQuill = null;
if (typeof window !== "undefined") {
  ReactQuill = require("react-quill");
  require("react-quill/dist/quill.snow.css");
}
import MDAlert from "components/MDAlert";
import ImageUpload from "components/ImageUpload";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { adminApi } from "services/api";

// Below md the DataTable shows each row as a card, which would print long text in full; clamp it
// there (column `clampLines`, default 3) so cards stay scannable. Desktop cells are unchanged.
function ClampedCell({ value, column }) {
  return (
    <MDBox
      component="span"
      color="inherit"
      sx={({ breakpoints }) => ({
        [breakpoints.down("md")]: {
          display: "-webkit-box",
          WebkitLineClamp: column.clampLines || 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        },
      })}
    >
      {value}
    </MDBox>
  );
}

ClampedCell.propTypes = {
  value: PropTypes.node,
  column: PropTypes.shape({ clampLines: PropTypes.number }).isRequired,
};

// Full-screen dialog (below sm): let the form content scroll between a pinned title and action bar.
const fullScreenForm = { display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 };

function TeamMembers() {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Save failures show inside the open dialog, not on the page behind it.
  const [dialogError, setDialogError] = useState("");
  const [success, setSuccess] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({
    name: "",
    position: "",
    bio: "",
    image: "",
    email: "",
    phone: "",
    socialLinks: {
      linkedin: "",
      twitter: "",
      facebook: "",
    },
  });

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getTeamMembers();
      setTeamMembers(data);
      setError("");
    } catch (err) {
      setError(
        err.response?.data?.error || err.response?.data?.message || "Failed to fetch team members"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setEditMode(false);
    setFormData({
      name: "",
      position: "",
      bio: "",
      image: "",
      email: "",
      phone: "",
      socialLinks: {
        linkedin: "",
        twitter: "",
        facebook: "",
      },
    });
    setDialogError("");
    setOpenDialog(true);
  };

  const handleEdit = (member) => {
    setEditMode(true);
    setCurrentId(member._id);
    setFormData({
      name: member.name,
      position: member.position || "",
      bio: member.bio || "",
      image: member.image || "",
      email: member.email || "",
      phone: member.phone || "",
      socialLinks: {
        linkedin: member.linkedin || member.socialLinks?.linkedin || "",
        twitter: member.twitter || member.socialLinks?.twitter || "",
        facebook: member.facebook || member.socialLinks?.facebook || "",
      },
    });
    setDialogError("");
    setOpenDialog(true);
    handleMenuClose();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setCurrentId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("social_")) {
      const socialKey = name.replace("social_", "");
      setFormData({
        ...formData,
        socialLinks: {
          ...formData.socialLinks,
          [socialKey]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDialogError("");
    setError("");
    setSuccess("");

    // Flatten socialLinks into top-level fields for backend compatibility
    const payload = {
      ...formData,
      linkedin: formData.socialLinks?.linkedin || "",
      twitter: formData.socialLinks?.twitter || "",
      facebook: formData.socialLinks?.facebook || "",
    };
    // Remove nested socialLinks if not needed by backend
    delete payload.socialLinks;

    try {
      if (editMode) {
        await adminApi.updateTeamMember(currentId, payload);
        setSuccess("Team member updated successfully");
      } else {
        await adminApi.createTeamMember(payload);
        setSuccess("Team member created successfully");
      }
      handleCloseDialog();
      fetchTeamMembers();
    } catch (err) {
      setDialogError(
        err.response?.data?.error || err.response?.data?.message || "Operation failed"
      );
    }
  };

  const handleDelete = async () => {
    if (!selectedMember) return;

    try {
      await adminApi.deleteTeamMember(selectedMember._id);
      setSuccess("Team member deleted successfully");
      fetchTeamMembers();
      handleMenuClose();
    } catch (err) {
      setError(
        err.response?.data?.error || err.response?.data?.message || "Failed to delete team member"
      );
    }
  };

  const handleMenuOpen = (event, member) => {
    setAnchorEl(event.currentTarget);
    setSelectedMember(member);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMember(null);
  };

  const columns = [
    {
      Header: "name",
      accessor: "name",
      width: "25%",
      align: "left",
      Cell: ClampedCell,
      clampLines: 2,
    },
    { Header: "position", accessor: "position", width: "25%", align: "left", Cell: ClampedCell },
    {
      Header: "email",
      accessor: "email",
      width: "30%",
      align: "left",
      Cell: ClampedCell,
      clampLines: 2,
    },
    { Header: "actions", accessor: "actions", width: "20%", align: "center" },
  ];

  const rows = teamMembers.map((member) => ({
    name: member.name,
    position: member.position,
    email: member.email || "N/A",
    actions: (
      <IconButton onClick={(e) => handleMenuOpen(e, member)}>
        <Icon>more_vert</Icon>
      </IconButton>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexWrap="wrap"
                gap={1.5}
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Team Members
                </MDTypography>
                <MDButton variant="contained" color="white" onClick={handleOpenDialog}>
                  Add Team Member
                </MDButton>
              </MDBox>

              {error && (
                <MDBox pt={2} px={2}>
                  <MDAlert color="error" dismissible onClose={() => setError("")}>
                    {error}
                  </MDAlert>
                </MDBox>
              )}

              {success && (
                <MDBox pt={2} px={2}>
                  <MDAlert color="success" dismissible onClose={() => setSuccess("")}>
                    {success}
                  </MDAlert>
                </MDBox>
              )}

              <MDBox pt={3}>
                {loading ? (
                  <MDBox p={3} textAlign="center">
                    <MDTypography variant="body2">Loading...</MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>{editMode ? "Edit Team Member" : "Add New Team Member"}</DialogTitle>
        <form onSubmit={handleSubmit} style={fullScreenDialog ? fullScreenForm : undefined}>
          <DialogContent>
            {dialogError && (
              <MDBox mb={2}>
                <MDAlert color="error" dismissible onClose={() => setDialogError("")}>
                  {dialogError}
                </MDAlert>
              </MDBox>
            )}
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                required
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label="Position"
                name="position"
                value={formData.position}
                onChange={handleChange}
                fullWidth
                required
              />
            </MDBox>
            <MDBox mb={2}>
              <label style={{ fontWeight: 500, marginBottom: 4, display: "block" }}>Bio</label>
              {ReactQuill ? (
                <ReactQuill
                  value={formData.bio}
                  onChange={(val) => setFormData({ ...formData, bio: val })}
                  theme="snow"
                  style={{ minHeight: 120, marginBottom: 8 }}
                />
              ) : (
                <MDInput
                  type="text"
                  label="Bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={4}
                />
              )}
            </MDBox>
            <MDBox mb={2}>
              <ImageUpload
                currentImage={formData.image}
                onImageChange={(url) => setFormData({ ...formData, image: url })}
                label="Profile Image"
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="tel"
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <MDTypography variant="caption" fontWeight="bold">
                Social Links
              </MDTypography>
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="url"
                label="LinkedIn URL"
                name="social_linkedin"
                value={formData.socialLinks.linkedin}
                onChange={handleChange}
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="url"
                label="Twitter URL"
                name="social_twitter"
                value={formData.socialLinks.twitter}
                onChange={handleChange}
                fullWidth
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="url"
                label="Facebook URL"
                name="social_facebook"
                value={formData.socialLinks.facebook}
                onChange={handleChange}
                fullWidth
              />
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCloseDialog} color="secondary">
              Cancel
            </MDButton>
            <MDButton type="submit" color="info">
              {editMode ? "Update" : "Create"}
            </MDButton>
          </DialogActions>
        </form>
      </Dialog>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleEdit(selectedMember)}>Edit</MenuItem>
        <MenuItem onClick={handleDelete}>Delete</MenuItem>
      </Menu>

      <Footer />
    </DashboardLayout>
  );
}

export default TeamMembers;
